// Cognitive Perception Field — animated radar that "scans" 6 dimensions
// of a capital project (schedule, cost, quality, HSE, progress, suppliers).
import { useEffect, useState } from 'react';

const STRATA = [
  { key: 'sched',    label: 'SCHEDULE',  sub: '847 ACTIVITIES', r: 105, ang: 220, color: '#5790C9' },
  { key: 'cost',     label: 'COST',      sub: '$510 M',        r: 145, ang: 320, color: '#F26B1F' },
  { key: 'quality',  label: 'QUALITY',   sub: '14,200 ITP',    r: 183, ang: 200, color: '#0E7C6B' },
  { key: 'hse',      label: 'HSE',       sub: '0 LTI',         r: 218, ang: 240, color: '#10B981' },
  { key: 'progress', label: 'PROGRESS',  sub: '62% VPR',       r: 250, ang: 140, color: '#5790C9' },
  { key: 'supplier', label: 'SUPPLIERS', sub: '247 ACTIVE',    r: 280, ang: 50,  color: '#F26B1F' },
];

const polar = (cx, cy, r, deg) => {
  const rad = (deg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

export default function PerceptionField() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 60);
    return () => clearInterval(t);
  }, []);
  const cx = 340, cy = 296;
  const scanAngle = (tick * 1.5) % 360;

  // 22 ambient data points, deterministic
  const points = Array.from({ length: 22 }, (_, i) => {
    const r = 80 + ((i * 37) % 200);
    const a = (i * 47) % 360;
    const [x, y] = polar(cx, cy, r, a);
    const isAttention = i % 7 === 0;
    return { x, y, isAttention, delay: (i * 0.43) % 6 };
  });

  return (
    <div className="relative w-full h-full">
      <svg viewBox="0 0 680 580" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <pattern id="dp-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#1E4E8C" strokeOpacity="0.15"/>
          </pattern>
          <radialGradient id="dp-fade" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#000" stopOpacity="1"/>
            <stop offset="70%" stopColor="#000" stopOpacity="0.55"/>
            <stop offset="100%" stopColor="#000" stopOpacity="0"/>
          </radialGradient>
          <mask id="dp-mask"><rect width="100%" height="100%" fill="url(#dp-fade)"/></mask>
          <radialGradient id="dp-cone" cx="0%" cy="50%" r="100%">
            <stop offset="0%" stopColor="#C2D5EE" stopOpacity="0.45"/>
            <stop offset="45%" stopColor="#5790C9" stopOpacity="0.22"/>
            <stop offset="100%" stopColor="#5790C9" stopOpacity="0.05"/>
          </radialGradient>
          <radialGradient id="dp-eye">
            <stop offset="0%" stopColor="#F26B1F" stopOpacity="1"/>
            <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0.4"/>
          </radialGradient>
        </defs>

        {/* Grid background */}
        <g mask="url(#dp-mask)">
          <rect x="0" y="26" width="680" height="532" fill="url(#dp-grid)"/>
        </g>

        {/* Heartbeat halo */}
        <circle cx={cx} cy={cy} r="40" fill="none" stroke="#F26B1F" strokeWidth="1" opacity="0.4">
          <animate attributeName="r" values="38;72;38" dur="3s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.4;0;0.4" dur="3s" repeatCount="indefinite"/>
        </circle>

        {/* Concentric measurement rings */}
        {STRATA.map(s => {
          const [lx, ly] = polar(cx, cy, s.r, s.ang);
          const anchor = lx < cx ? 'end' : 'start';
          const dx = lx < cx ? -8 : 8;
          return (
            <g key={s.key}>
              <circle cx={cx} cy={cy} r={s.r} fill="none" stroke={s.color} strokeWidth="0.6" strokeDasharray="2 4" opacity="0.5"/>
              <text x={lx + dx} y={ly} textAnchor={anchor} fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="600" fill="#E5E7EB" letterSpacing="0.1em">{s.label}</text>
              <text x={lx + dx} y={ly + 14} textAnchor={anchor} fontFamily="'JetBrains Mono', monospace" fontSize="9" fill="#9AA2B1" letterSpacing="0.05em">{s.sub}</text>
            </g>
          );
        })}

        {/* Rotating scan cone */}
        <g transform={`rotate(${scanAngle} ${cx} ${cy})`}>
          <path d={`M ${cx} ${cy} L ${cx + 280} ${cy - 80} A 280 280 0 0 1 ${cx + 280} ${cy + 80} Z`} fill="url(#dp-cone)"/>
        </g>

        {/* Data points */}
        {points.map((p, i) => (
          <rect key={i} x={p.x - 4} y={p.y - 4} width="8" height="8"
            transform={`rotate(45 ${p.x} ${p.y})`}
            fill={p.isAttention ? '#F26B1F' : '#5790C9'}
            opacity={p.isAttention ? 0.95 : 0.6}>
            {p.isAttention && (
              <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" begin={`${p.delay}s`}/>
            )}
          </rect>
        ))}

        {/* Central attention node — the "eye" of Panorix */}
        <circle cx={cx} cy={cy} r="22" fill="url(#dp-eye)"/>
        <circle cx={cx} cy={cy} r="10" fill="#F26B1F"/>
        <circle cx={cx} cy={cy} r="4" fill="#FFF"/>
        <text x={cx} y={cy + 50} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fontWeight="700" fill="#F26B1F" letterSpacing="0.25em">PANORIX</text>
        <text x={cx} y={cy + 64} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="#9AA2B1" letterSpacing="0.2em">SCAN · CONNECT · SEE</text>
      </svg>
    </div>
  );
}
